import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import type {
  BuyerSmartImportPreviewBuyerDto,
  BuyerSmartImportPreviewChip,
  BuyerSmartImportPreviewDto,
  BuyerSmartImportRejectedRowDto,
} from "@/api/buyerSmartImport";
import {
  Button,
  FilterChips,
  StatusBadge,
  SubTable,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { BUYER_TYPE_LABELS } from "@/pages/buyers/sections/buyerSectionConfig";
import { PREVIEW_CHIPS, smartImportCopy } from "./smartImportCopy";
import { buildRejectedRowsCsv, rejectedRowsFileName } from "./rejectedRowsCsv";

/**
 * The preview (D4): at most ten buyers under each chip, each shown EXACTLY as
 * it will be created — full Notes included — and every rejected row with its
 * raw cells.
 *
 * <p>🔴 <b>Notes is rendered in full and never truncated.</b> Notes is where the
 * composed inquiry history lands (D7), so a preview that elided it would be a
 * preview of something other than what gets written — which is the one thing a
 * preview may not be.</p>
 */

/** How many buyers a chip shows. The server caps at the same number (D4). */
export const PREVIEW_CHIP_CAP = 10;

/**
 * 🔴 <b>Merged is `sourceRowCount > 1`, never `inquiryCount > 1`.</b> A row
 * carrying a phone number and no message still folds into a buyer and adds
 * nothing to the inquiry block, so a buyer merged from two rows where only one
 * carried an inquiry has two source rows and one inquiry — and an inquiry-count
 * test would silently drop it out of the chip that exists to show it.
 */
export function buyersForChip(
  buyers: readonly BuyerSmartImportPreviewBuyerDto[],
  chip: BuyerSmartImportPreviewChip,
): BuyerSmartImportPreviewBuyerDto[] {
  switch (chip) {
    case "merged":
      return buyers.filter((b) => b.sourceRowCount > 1).slice(0, PREVIEW_CHIP_CAP);
    case "flagged":
      return buyers.filter((b) => b.duplicateOf != null).slice(0, PREVIEW_CHIP_CAP);
    case "rejected":
      return [];
    default:
      return buyers.slice(0, PREVIEW_CHIP_CAP);
  }
}

export interface SmartImportPreviewProps {
  preview: BuyerSmartImportPreviewDto;
  /** Names the rejected-rows download after the file it came from. */
  sourceName: string;
}

export function SmartImportPreview({ preview, sourceName }: SmartImportPreviewProps) {
  const [chip, setChip] = useState<BuyerSmartImportPreviewChip>("all");

  const chips = useMemo(
    () =>
      PREVIEW_CHIPS.map((c) => ({
        id: c.id,
        label: c.label,
        count:
          c.id === "rejected"
            ? preview.rejectedRows.length
            : buyersForChip(preview.buyers, c.id).length,
      })),
    [preview],
  );

  const shown = buyersForChip(preview.buyers, chip);

  return (
    <section aria-label={smartImportCopy.previewTitle} data-testid="smart-import-preview">
      <h3 className="font-display font-semibold text-label uppercase tracking-label text-gold-shadow mb-space-2">
        {smartImportCopy.previewTitle}
      </h3>

      <FilterChips
        chips={chips}
        activeId={chip}
        label={smartImportCopy.previewTitle}
        onChange={(id) => setChip(id as BuyerSmartImportPreviewChip)}
      />
      <p className="mt-space-2 text-caption text-gray-mid">{smartImportCopy.previewCap}</p>

      {chip === "rejected" ? (
        <RejectedRows rows={preview.rejectedRows} sourceName={sourceName} />
      ) : (
        <div className="mt-space-3 grid gap-space-3" data-testid="preview-buyers">
          {shown.length === 0 ? (
            <p className="text-small text-gray-mid">Nothing under this heading.</p>
          ) : (
            shown.map((b, i) => <PreviewBuyerCard key={`preview-buyer-${i}`} buyer={b} index={i} />)
          )}
        </div>
      )}
    </section>
  );
}

function PreviewBuyerCard({
  buyer,
  index,
}: {
  buyer: BuyerSmartImportPreviewBuyerDto;
  index: number;
}) {
  const name = [buyer.firstName, buyer.lastName].filter(Boolean).join(" ").trim();

  return (
    <article
      className="border border-border-light rounded-md bg-white-pure p-space-3"
      data-testid={`preview-buyer-${index}`}
    >
      <div className="flex flex-wrap items-center gap-space-2 mb-space-2">
        <span className="font-medium text-onyx">{name || "(no name)"}</span>
        {buyer.sourceRowCount > 1 && (
          <StatusBadge tone="info" data-testid={`preview-buyer-${index}-merged`}>
            {buyer.sourceRowCount} rows merged
          </StatusBadge>
        )}
        {buyer.inquiryCount > 0 && (
          <span className="text-caption text-gray-mid">
            {buyer.inquiryCount} {smartImportCopy.previewInquiries}
          </span>
        )}
        {buyer.duplicateOf && (
          <StatusBadge tone="warn" data-testid={`preview-buyer-${index}-duplicate`}>
            {buyer.duplicateIsExisting
              ? `${smartImportCopy.duplicatesExisting}: ${buyer.duplicateOf}`
              : `${smartImportCopy.duplicatesWithinFile}: ${buyer.duplicateOf}`}
          </StatusBadge>
        )}
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-space-3 gap-y-space-1 text-small">
        <Field label="Email" value={buyer.email} />
        <Field label="Phone" value={buyer.phone} />
        <Field label="City" value={buyer.city} />
        <Field label="State" value={buyer.state} />
        <Field label="ZIP" value={buyer.zip} />
        <Field label="Source" value={buyer.source} />
        <Field label="Source detail" value={buyer.sourceDetail} />
        <Field label="Stated price band" value={buyer.statedPriceBand} />
        <Field label="Liquid capital" value={money(buyer.liquidCapital)} />
        <Field label="Net worth" value={money(buyer.netWorth)} />
        <Field label="Max purchase price" value={money(buyer.maxPurchasePrice)} />
        <Field label="Down payment available" value={money(buyer.downPaymentAvailable)} />
        <Field label="Target industries" value={buyer.targetIndustries} />
        <Field label="Target geographies" value={buyer.targetGeographies} />
        <Field label="Timeline to purchase" value={buyer.timelineToPurchase} />
        <Field label="Industry experience" value={buyer.industryExperience} />
        <Field
          label="Buyer type"
          value={buyer.buyerType ? BUYER_TYPE_LABELS[buyer.buyerType] : null}
        />
        <Field label="Company" value={buyer.acquiringCompanyName} />
        <Field label="Title" value={buyer.acquiringCompanyTitle} />
        <Field label="Website" value={buyer.acquiringCompanyWebsite} />
      </dl>

      <div className="mt-space-2">
        <div className="text-caption uppercase tracking-wide text-gray-mid">
          {smartImportCopy.previewNotesLabel}
        </div>
        <p
          className="mt-space-1 whitespace-pre-wrap break-words text-small text-onyx"
          data-testid={`preview-buyer-${index}-notes`}
        >
          {buyer.notes?.trim() ? buyer.notes : "—"}
        </p>
      </div>
    </article>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value.trim() === "") return null;
  return (
    <>
      <dt className="text-gray-mid">{label}</dt>
      <dd className="text-onyx break-words">{value}</dd>
    </>
  );
}

function money(value: number | null | undefined): string | null {
  return value == null ? null : formatCurrency(value);
}

function RejectedRows({
  rows,
  sourceName,
}: {
  rows: readonly BuyerSmartImportRejectedRowDto[];
  sourceName: string;
}) {
  const columns: SubTableColumn<BuyerSmartImportRejectedRowDto>[] = [
    { key: "tab", header: "Tab", render: (r) => r.tabName },
    { key: "row", header: "Row", align: "right", render: (r) => r.rowNumber },
    {
      key: "reason",
      header: "Reason",
      render: (r) => <span className="text-status-error">{r.reason}</span>,
    },
    {
      key: "cells",
      header: "Raw cells",
      render: (r) => (
        <span className="font-mono text-caption text-gray-mid break-all">
          {r.cells.join(" | ")}
        </span>
      ),
    },
  ];

  return (
    <div className="mt-space-3 grid gap-space-2" data-testid="preview-rejected-rows">
      <SubTable
        caption={smartImportCopy.rejectedRowsTitle}
        columns={columns}
        rows={[...rows]}
        rowKey={(r) => `${r.tabName}-${r.rowNumber}`}
        empty={{ message: "No rows were rejected." }}
      />
      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={rows.length === 0}
          onClick={() => downloadRejectedRows(rows, sourceName)}
        >
          <Download size={14} strokeWidth={1.5} aria-hidden />
          {smartImportCopy.downloadRejected}
        </Button>
      </div>
    </div>
  );
}

/**
 * Build the CSV in the browser and hand it straight to the operator.
 *
 * <p>Nothing about this touches the server: the rejected rows are raw cells out
 * of a file that is never persisted (D5), so the preview on screen is the only
 * place they exist.</p>
 */
export function downloadRejectedRows(
  rows: readonly BuyerSmartImportRejectedRowDto[],
  sourceName: string,
): void {
  const blob = new Blob([buildRejectedRowsCsv(rows)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = rejectedRowsFileName(sourceName);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
